import React, { useState, useEffect } from 'react';
import "../../css/NoticesAdd.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FaBullhorn, FaSearch, FaTimes, FaFilter, FaPen, FaTrash, FaEye,
    FaPlusCircle, FaCalendarAlt, FaUserTag, FaDownload, FaPaperclip,
    FaFileAlt, FaExclamationTriangle
} from 'react-icons/fa';
import Loader from "../../components/Loader";

function NoticesAdd() {
    const [notices, setNotices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [filterCategory, setFilterCategory] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [noticeToDelete, setNoticeToDelete] = useState(null);
    // Form states
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: 'general',
        targetAudience: ['all'],
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        priority: 'normal',
        attachment: null
    });
    const confirmDelete = (notice) => {
        setNoticeToDelete(notice);
        setShowDeleteModal(true);
    };
    // Categories and audience options
    const categories = [
        { id: 'general', name: 'General' },
        { id: 'academic', name: 'Academic' },
        { id: 'examination', name: 'Examination' },
        { id: 'event', name: 'Event' },
        { id: 'holiday', name: 'Holiday' },
        { id: 'emergency', name: 'Emergency' },
    ];

    const audienceOptions = [
        { id: 'all', name: 'Everyone' },
        { id: 'students', name: 'Students' },
        { id: 'teachers', name: 'Teachers' },
        { id: 'guardians', name: 'Guardians' },
        { id: 'staff', name: 'Administrative Staff' },
    ];

    const priorityOptions = [
        { id: 'low', name: 'Low' },
        { id: 'normal', name: 'Normal' },
        { id: 'high', name: 'High' },
        { id: 'urgent', name: 'Urgent' },
    ];

    // Fetch all notices
    const fetchNotices = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }

        try {
            // Replace with your actual API endpoint
            const response = await fetch("https://schoolpro-api.uniums.com/api/notices", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch notices");
            }

            const data = await response.json();
            setNotices(data);
        } catch (error) {
            console.error("Error fetching notices:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };
    const handleAudienceChange = (e) => {
        const { value, checked } = e.target;

        if (value === 'all' && checked) {
            setFormData({
                ...formData,
                targetAudience: ['all']
            });
        } else {
            let newAudience = [...formData.targetAudience];

            if (checked) {
                newAudience = newAudience.filter(item => item !== 'all');
                newAudience.push(value);
            } else {
    
                newAudience = newAudience.filter(item => item !== value);
            }
            if (newAudience.length === 0) {
                newAudience = ['all'];
            }

            setFormData({
                ...formData,
                targetAudience: newAudience
            });
        }
    };
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData({
                    ...formData,
                    attachment: reader.result 
                });
            };
            reader.readAsDataURL(file);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }

        try {
            const noticeData = { ...formData };

            const response = await fetch('https://schoolpro-api.uniums.com/api/notices/add', {
                method: 'POST',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(noticeData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create notice");
            }

            toast.success("Notice created successfully!");
            fetchNotices();
            setShowAddModal(false);
            resetForm();
        } catch (error) {
            console.error("Error creating notice:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    // Edit notice
    const handleEdit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }

        try {
            // Prepare the data with attachment if it exists
            const updateData = { ...formData };

            const response = await fetch(`https://schoolpro-api.uniums.com/api/notices/update/${selectedNotice._id}`, {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(updateData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update notice");
            }

            toast.success("Notice updated successfully!");
            fetchNotices();
            setShowEditModal(false);
        } catch (error) {
            console.error("Error updating notice:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Delete notice
    const executeDelete = async () => {
        if (!noticeToDelete) return;

        setIsLoading(true);
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/notices/delete/${noticeToDelete._id}`, {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete notice");
            }

            toast.success("Notice deleted successfully!");
            fetchNotices();
            setShowDeleteModal(false);
            setNoticeToDelete(null);
        } catch (error) {
            console.error("Error deleting notice:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    const handleDelete = (noticeId) => {
        const notice = notices.find(n => n._id === noticeId);
        if (notice) {
            confirmDelete(notice);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            content: '',
            category: 'general',
            targetAudience: ['all'],
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            priority: 'normal',
            attachment: null
        });
    };

    // Open edit modal and populate form
    const openEditModal = (notice) => {
        setSelectedNotice(notice);
        setFormData({
            title: notice.title,
            content: notice.content,
            category: notice.category,
            targetAudience: notice.targetAudience,
            startDate: notice.startDate.split('T')[0],
            endDate: notice.endDate ? notice.endDate.split('T')[0] : '',
            priority: notice.priority,
            attachment: notice.attachmentUrl || null
        });
        setShowEditModal(true);
    };

    // Open view modal
    const openViewModal = (notice) => {
        setSelectedNotice(notice);
        setShowViewModal(true);
    };

    // Get filtered notices
    const getFilteredNotices = () => {
        if (!notices.length) return [];

        return notices.filter(notice => {
            const matchesSearch =
                notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notice.content.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesCategory =
                filterCategory === 'all' ||
                notice.category === filterCategory;

            return matchesSearch && matchesCategory;
        });
    };

    // Get sorted notices
    const getSortedNotices = () => {
        const filteredData = getFilteredNotices();

        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            if (sortConfig.key === 'createdAt' || sortConfig.key === 'startDate') {
                const dateA = new Date(a[sortConfig.key]);
                const dateB = new Date(b[sortConfig.key]);

                if (dateA < dateB) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (dateA > dateB) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            } else {
                if (a[sortConfig.key].toLowerCase() < b[sortConfig.key].toLowerCase()) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key].toLowerCase() > b[sortConfig.key].toLowerCase()) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            }
        });
    };

    // Request sort
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort indicator
    const getSortDirectionIndicator = (column) => {
        if (sortConfig.key !== column) return null;
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    // Format date
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Get category label
    const getCategoryLabel = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Unknown';
    };

    // Get audience labels
    const getAudienceLabels = (audienceArr) => {
        if (audienceArr.includes('all')) {
            return 'Everyone';
        }

        return audienceArr.map(aud => {
            const option = audienceOptions.find(opt => opt.id === aud);
            return option ? option.name : aud;
        }).join(', ');
    };

    // Get priority badge class
    const getPriorityBadgeClass = (priority) => {
        switch (priority) {
            case 'low': return 'noticeadd-priority-low';
            case 'normal': return 'noticeadd-priority-normal';
            case 'high': return 'noticeadd-priority-high';
            case 'urgent': return 'noticeadd-priority-urgent';
            default: return 'noticeadd-priority-normal';
        }
    };

    // Check if a notice is active
    const isNoticeActive = (notice) => {
        const now = new Date();
        const startDate = new Date(notice.startDate);
        const endDate = notice.endDate ? new Date(notice.endDate) : null;

        return startDate <= now && (!endDate || endDate >= now);
    };

    // Get status badge class
    const getStatusBadgeClass = (notice) => {
        if (isNoticeActive(notice)) {
            return 'noticeadd-status-active';
        }

        const now = new Date();
        const startDate = new Date(notice.startDate);

        if (startDate > now) {
            return 'noticeadd-status-scheduled';
        }

        return 'noticeadd-status-expired';
    };

    // Get status text
    const getStatusText = (notice) => {
        if (isNoticeActive(notice)) {
            return 'Active';
        }

        const now = new Date();
        const startDate = new Date(notice.startDate);

        if (startDate > now) {
            return 'Scheduled';
        }

        return 'Expired';
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilterCategory('all');
        setSortConfig({ key: 'createdAt', direction: 'desc' });
    };

    // Remove file attachment
    const removeAttachment = () => {
        setFormData({
            ...formData,
            attachment: null
        });
        // Reset file input
        const fileInput = document.getElementById('attachment');
        if (fileInput) fileInput.value = '';
    };

    // Effect hooks
    useEffect(() => {
        fetchNotices();
    }, []);

    return (
        <div className="noticeadd-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            <div className="noticeadd-header">
                <div className="noticeadd-title-container">
                    <FaBullhorn className="noticeadd-title-icon" />
                    <h1 className="noticeadd-title">Notice Management</h1>
                </div>

                <div className="noticeadd-stats">
                    <div className="noticeadd-stat-box">
                        <div className="noticeadd-stat-icon noticeadd-stat-icon-total">
                            <FaBullhorn />
                        </div>
                        <div className="noticeadd-stat-info">
                            <span className="noticeadd-stat-number">{notices.length}</span>
                            <span className="noticeadd-stat-label">Total Notices</span>
                        </div>
                    </div>
                    <div className="noticeadd-stat-box">
                        <div className="noticeadd-stat-icon noticeadd-stat-icon-active">
                            <FaBullhorn />
                        </div>
                        <div className="noticeadd-stat-info">
                            <span className="noticeadd-stat-number">
                                {notices.filter(notice => isNoticeActive(notice)).length}
                            </span>
                            <span className="noticeadd-stat-label">Active Notices</span>
                        </div>
                    </div>
                    <div className="noticeadd-stat-box">
                        <div className="noticeadd-stat-icon noticeadd-stat-icon-scheduled">
                            <FaCalendarAlt />
                        </div>
                        <div className="noticeadd-stat-info">
                            <span className="noticeadd-stat-number">
                                {notices.filter(notice => {
                                    const now = new Date();
                                    const startDate = new Date(notice.startDate);
                                    return startDate > now;
                                }).length}
                            </span>
                            <span className="noticeadd-stat-label">Scheduled Notices</span>
                        </div>
                    </div>
                    <div className="noticeadd-stat-box">
                        <div className="noticeadd-stat-icon noticeadd-stat-icon-expired">
                            <FaCalendarAlt />
                        </div>
                        <div className="noticeadd-stat-info">
                            <span className="noticeadd-stat-number">
                                {notices.filter(notice => {
                                    const now = new Date();
                                    const endDate = notice.endDate ? new Date(notice.endDate) : null;
                                    const startDate = new Date(notice.startDate);
                                    return (endDate && endDate < now) || (startDate > now && !isNoticeActive(notice));
                                }).length}
                            </span>
                            <span className="noticeadd-stat-label">Expired Notices</span>
                        </div>
                    </div>
                </div>

                <div className="noticeadd-actions">
                    <button
                        className="noticeadd-btn noticeadd-btn-primary"
                        onClick={() => {
                            resetForm();
                            setShowAddModal(true);
                        }}
                    >
                        <FaPlusCircle /> Add New Notice
                    </button>
                </div>
            </div>

            <div className="noticeadd-filters">
                <div className="noticeadd-filter-row">
                    <div className="noticeadd-filter-group">
                        <label className="noticeadd-filter-label">Filter by Category</label>
                        <select
                            className="noticeadd-filter-select"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="noticeadd-filter-group">
                        <label className="noticeadd-filter-label">Sort By</label>
                        <select
                            className="noticeadd-filter-select"
                            value={`${sortConfig.key}-${sortConfig.direction}`}
                            onChange={(e) => {
                                const [key, direction] = e.target.value.split('-');
                                setSortConfig({ key, direction });
                            }}
                        >
                            <option value="createdAt-desc">Date Created (Newest First)</option>
                            <option value="createdAt-asc">Date Created (Oldest First)</option>
                            <option value="startDate-desc">Start Date (Newest First)</option>
                            <option value="startDate-asc">Start Date (Oldest First)</option>
                            <option value="title-asc">Title (A-Z)</option>
                            <option value="title-desc">Title (Z-A)</option>
                        </select>
                    </div>
                </div>

                <div className="noticeadd-search-container">
                    <FaSearch className="noticeadd-search-icon" />
                    <input
                        type="text"
                        className="noticeadd-search-input"
                        placeholder="Search notices by title or content..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="noticeadd-search-clear" onClick={() => setSearchTerm('')}>
                            <FaTimes />
                        </button>
                    )}
                    <button className="noticeadd-btn noticeadd-btn-clear-filters" onClick={clearFilters}>
                        <FaTimes /> Clear Filters
                    </button>
                </div>
            </div>

            {isLoading && !showAddModal && !showEditModal ? (
                <div className="noticeadd-loading">
                    <Loader />
                    <p>Loading notices...</p>
                </div>
            ) : (
                <div className="noticeadd-content">
                    {notices.length === 0 ? (
                        <div className="noticeadd-empty">
                            <FaBullhorn className="noticeadd-empty-icon" />
                            <h2>No Notices Available</h2>
                            <p>Start by creating a new notice using the "Add New Notice" button above.</p>
                        </div>
                    ) : getSortedNotices().length === 0 ? (
                        <div className="noticeadd-no-results">
                            <h3>No Matching Notices</h3>
                            <p>No notices match your current search or filter criteria.</p>
                            <button className="noticeadd-btn noticeadd-btn-secondary" onClick={clearFilters}>
                                Clear Filters
                            </button>
                        </div>
                    ) : (
                        <div className="noticeadd-table-container">
                            <table className="noticeadd-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => requestSort('title')}>
                                            Title {getSortDirectionIndicator('title')}
                                        </th>
                                        <th>Category</th>
                                        <th>Target Audience</th>
                                        <th onClick={() => requestSort('startDate')}>
                                            Date {getSortDirectionIndicator('startDate')}
                                        </th>
                                        <th>Priority</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getSortedNotices().map((notice) => (
                                        <tr key={notice._id} className={`noticeadd-row-${getStatusText(notice).toLowerCase()}`}>
                                            <td className="noticeadd-notice-title">
                                                {notice.title}
                                                {notice.attachmentUrl && (
                                                    <span className="noticeadd-attachment-indicator">
                                                        <FaPaperclip />
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`noticeadd-category-badge noticeadd-category-${notice.category}`}>
                                                    {getCategoryLabel(notice.category)}
                                                </span>
                                            </td>
                                            <td className="noticeadd-audience">
                                                <FaUserTag className="noticeadd-audience-icon" />
                                                {getAudienceLabels(notice.targetAudience)}
                                            </td>
                                            <td>
                                                <div className="noticeadd-dates">
                                                    <div className="noticeadd-date">
                                                        <span className="noticeadd-date-label">Start:</span>
                                                        <span className="noticeadd-date-value">{formatDate(notice.startDate)}</span>
                                                    </div>
                                                    {notice.endDate && (
                                                        <div className="noticeadd-date">
                                                            <span className="noticeadd-date-label">End:</span>
                                                            <span className="noticeadd-date-value">{formatDate(notice.endDate)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`noticeadd-priority-badge ${getPriorityBadgeClass(notice.priority)}`}>
                                                    {notice.priority.charAt(0).toUpperCase() + notice.priority.slice(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`noticeadd-status-badge ${getStatusBadgeClass(notice)}`}>
                                                    {getStatusText(notice)}
                                                </span>
                                            </td>
                                            <td className="noticeadd-actions-cell">
                                                <button
                                                    className="noticeadd-action-btn noticeadd-action-view"
                                                    onClick={() => openViewModal(notice)}
                                                    title="View Notice"
                                                >
                                                    <FaEye />
                                                </button>
                                                <button
                                                    className="noticeadd-action-btn noticeadd-action-edit"
                                                    onClick={() => openEditModal(notice)}
                                                    title="Edit Notice"
                                                >
                                                    <FaPen />
                                                </button>
                                                <button
                                                    className="noticeadd-action-btn noticeadd-action-delete"
                                                    onClick={() => handleDelete(notice._id)}
                                                    title="Delete Notice"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Add Notice Modal */}
            {showAddModal && (
                <div className="noticeadd-modal-overlay">
                    <div className="noticeadd-modal">
                        <div className="noticeadd-modal-header">
                            <h2 className="noticeadd-modal-title">Add New Notice</h2>
                            <button
                                className="noticeadd-modal-close"
                                onClick={() => setShowAddModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="noticeadd-modal-content">
                            <form onSubmit={handleSubmit} className="noticeadd-form">
                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Notice Title <span className="noticeadd-required">*</span></label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="noticeadd-form-input"
                                        placeholder="Enter notice title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="noticeadd-form-row">
                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">Category <span className="noticeadd-required">*</span></label>
                                        <select
                                            name="category"
                                            className="noticeadd-form-select"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {categories.map(category => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">Priority <span className="noticeadd-required">*</span></label>
                                        <select
                                            name="priority"
                                            className="noticeadd-form-select"
                                            value={formData.priority}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {priorityOptions.map(priority => (
                                                <option key={priority.id} value={priority.id}>
                                                    {priority.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="noticeadd-form-row">
                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">Start Date <span className="noticeadd-required">*</span></label>
                                        <input
                                            type="date"
                                            name="startDate"
                                            className="noticeadd-form-input"
                                            value={formData.startDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">End Date</label>
                                        <input
                                            type="date"
                                            name="endDate"
                                            className="noticeadd-form-input"
                                            value={formData.endDate}
                                            onChange={handleInputChange}
                                            min={formData.startDate}
                                        />
                                        <small className="noticeadd-form-hint">Leave blank for notices without expiry</small>
                                    </div>
                                </div>

                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Target Audience <span className="noticeadd-required">*</span></label>
                                    <div className="noticeadd-checkbox-group">
                                        {audienceOptions.map(option => (
                                            <label key={option.id} className="noticeadd-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    value={option.id}
                                                    checked={formData.targetAudience.includes(option.id)}

                                                    onChange={handleAudienceChange}
                                                    className="noticeadd-checkbox-input"
                                                />
                                                {option.name}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Notice Content <span className="noticeadd-required">*</span></label>
                                    <textarea
                                        name="content"
                                        className="noticeadd-form-textarea"
                                        placeholder="Enter notice content"
                                        value={formData.content}
                                        onChange={handleInputChange}
                                        required
                                        rows={5}
                                    ></textarea>
                                </div>

                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Attachment</label>
                                    <div className="noticeadd-file-input-container">
                                        <input
                                            type="file"
                                            id="attachment"
                                            name="attachment"
                                            className="noticeadd-file-input"
                                            onChange={handleFileChange}
                                        />
                                        <label htmlFor="attachment" className="noticeadd-file-label">
                                            <FaPaperclip /> Choose File
                                        </label>
                                        <span className="noticeadd-file-name">
                                            {formData.attachment ?
                                                (typeof formData.attachment === 'string' ?
                                                    formData.attachment.split('/').pop() :
                                                    formData.attachment.name) :
                                                'No file chosen'}
                                        </span>
                                        {formData.attachment && (
                                            <button
                                                type="button"
                                                className="noticeadd-remove-file"
                                                onClick={removeAttachment}
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                    </div>
                                    <small className="noticeadd-form-hint">Max file size: 5MB. Supported formats: PDF, DOC, DOCX, JPG, PNG</small>
                                </div>

                                <div className="noticeadd-form-actions">
                                    <button
                                        type="button"
                                        className="noticeadd-btn noticeadd-btn-secondary"
                                        onClick={() => setShowAddModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="noticeadd-btn noticeadd-btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : 'Save Notice'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Notice Modal */}
            {showEditModal && selectedNotice && (
                <div className="noticeadd-modal-overlay">
                    <div className="noticeadd-modal">
                        <div className="noticeadd-modal-header">
                            <h2 className="noticeadd-modal-title">Edit Notice</h2>
                            <button
                                className="noticeadd-modal-close"
                                onClick={() => setShowEditModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="noticeadd-modal-content">
                            <form onSubmit={handleEdit} className="noticeadd-form">
                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Notice Title <span className="noticeadd-required">*</span></label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="noticeadd-form-input"
                                        placeholder="Enter notice title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="noticeadd-form-row">
                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">Category <span className="noticeadd-required">*</span></label>
                                        <select
                                            name="category"
                                            className="noticeadd-form-select"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {categories.map(category => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">Priority <span className="noticeadd-required">*</span></label>
                                        <select
                                            name="priority"
                                            className="noticeadd-form-select"
                                            value={formData.priority}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {priorityOptions.map(priority => (
                                                <option key={priority.id} value={priority.id}>
                                                    {priority.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="noticeadd-form-row">
                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">Start Date <span className="noticeadd-required">*</span></label>
                                        <input
                                            type="date"
                                            name="startDate"
                                            className="noticeadd-form-input"
                                            value={formData.startDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="noticeadd-form-group">
                                        <label className="noticeadd-form-label">End Date</label>
                                        <input
                                            type="date"
                                            name="endDate"
                                            className="noticeadd-form-input"
                                            value={formData.endDate}
                                            onChange={handleInputChange}
                                            min={formData.startDate}
                                        />
                                        <small className="noticeadd-form-hint">Leave blank for notices without expiry</small>
                                    </div>
                                </div>

                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Target Audience <span className="noticeadd-required">*</span></label>
                                    <div className="noticeadd-checkbox-group">
                                        {audienceOptions.map(option => (
                                            <label key={option.id} className="noticeadd-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    value={option.id}
                                                    checked={formData.targetAudience.includes(option.id)}
                                                    onChange={handleAudienceChange}
                                                    className="noticeadd-checkbox-input"
                                                />
                                                {option.name}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Notice Content <span className="noticeadd-required">*</span></label>
                                    <textarea
                                        name="content"
                                        className="noticeadd-form-textarea"
                                        placeholder="Enter notice content"
                                        value={formData.content}
                                        onChange={handleInputChange}
                                        required
                                        rows={5}
                                    ></textarea>
                                </div>

                                <div className="noticeadd-form-group">
                                    <label className="noticeadd-form-label">Attachment</label>
                                    <div className="noticeadd-file-input-container">
                                        <input
                                            type="file"
                                            id="edit-attachment"
                                            name="attachment"
                                            className="noticeadd-file-input"
                                            onChange={handleFileChange}
                                        />
                                        <label htmlFor="edit-attachment" className="noticeadd-file-label">
                                            <FaPaperclip /> {typeof formData.attachment === 'string' && formData.attachment ? 'Change File' : 'Choose File'}
                                        </label>
                                        <span className="noticeadd-file-name">
                                            {formData.attachment ?
                                                (typeof formData.attachment === 'string' ?
                                                    formData.attachment.split('/').pop() :
                                                    formData.attachment.name) :
                                                'No file chosen'}
                                        </span>
                                        {formData.attachment && (
                                            <button
                                                type="button"
                                                className="noticeadd-remove-file"
                                                onClick={removeAttachment}
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                    </div>
                                    <small className="noticeadd-form-hint">Max file size: 5MB. Supported formats: PDF, DOC, DOCX, JPG, PNG</small>
                                </div>

                                <div className="noticeadd-form-actions">
                                    <button
                                        type="button"
                                        className="noticeadd-btn noticeadd-btn-secondary"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="noticeadd-btn noticeadd-btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Updating...' : 'Update Notice'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* View Notice Modal */}
            {showViewModal && selectedNotice && (
                <div className="noticeadd-modal-overlay">
                    <div className="noticeadd-modal noticeadd-view-modal">
                        <div className="noticeadd-modal-header">
                            <h2 className="noticeadd-modal-title">View Notice</h2>
                            <button
                                className="noticeadd-modal-close"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="noticeadd-modal-content">
                            <div className="noticeadd-view-content">
                                <div className="noticeadd-view-header">
                                    <h3 className="noticeadd-view-title">{selectedNotice.title}</h3>
                                    <div className="noticeadd-view-meta">
                                        <span className={`noticeadd-priority-badge ${getPriorityBadgeClass(selectedNotice.priority)}`}>
                                            {selectedNotice.priority.charAt(0).toUpperCase() + selectedNotice.priority.slice(1)} Priority
                                        </span>
                                        <span className={`noticeadd-status-badge ${getStatusBadgeClass(selectedNotice)}`}>
                                            {getStatusText(selectedNotice)}
                                        </span>
                                        <span className={`noticeadd-category-badge noticeadd-category-${selectedNotice.category}`}>
                                            {getCategoryLabel(selectedNotice.category)}
                                        </span>
                                    </div>
                                </div>

                                <div className="noticeadd-view-details">
                                    <div className="noticeadd-view-detail">
                                        <span className="noticeadd-view-label"><FaCalendarAlt /> Posted:</span>
                                        <span className="noticeadd-view-value">{formatDate(selectedNotice.createdAt)}</span>
                                    </div>
                                    <div className="noticeadd-view-detail">
                                        <span className="noticeadd-view-label"><FaCalendarAlt /> Active From:</span>
                                        <span className="noticeadd-view-value">{formatDate(selectedNotice.startDate)}</span>
                                    </div>
                                    {selectedNotice.endDate && (
                                        <div className="noticeadd-view-detail">
                                            <span className="noticeadd-view-label"><FaCalendarAlt /> Until:</span>
                                            <span className="noticeadd-view-value">{formatDate(selectedNotice.endDate)}</span>
                                        </div>
                                    )}
                                    <div className="noticeadd-view-detail">
                                        <span className="noticeadd-view-label"><FaUserTag /> Audience:</span>
                                        <span className="noticeadd-view-value">{getAudienceLabels(selectedNotice.targetAudience)}</span>
                                    </div>
                                </div>

                                <div className="noticeadd-view-body">
                                    <h4 className="noticeadd-view-section-title">Notice Content</h4>
                                    <div className="noticeadd-view-text">
                                        {selectedNotice.content}
                                    </div>
                                </div>

                                {selectedNotice.attachmentUrl && (
                                    <div className="noticeadd-view-attachment">
                                        <h4 className="noticeadd-view-section-title">Attachment</h4>
                                        <a
                                            href={selectedNotice.attachmentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="noticeadd-attachment-link"
                                        >
                                            <FaFileAlt className="noticeadd-attachment-icon" />
                                            <span>{selectedNotice.attachmentUrl.split('/').pop()}</span>
                                            <FaDownload className="noticeadd-download-icon" />
                                        </a>
                                    </div>
                                )}

                                <div className="noticeadd-view-actions">
                                    <button
                                        className="noticeadd-btn noticeadd-btn-secondary"
                                        onClick={() => {
                                            setShowViewModal(false);
                                            openEditModal(selectedNotice);
                                        }}
                                    >
                                        <FaPen /> Edit Notice
                                    </button>
                                    <button
                                        className="noticeadd-btn noticeadd-btn-danger"
                                        onClick={() => {
                                            setShowViewModal(false);
                                            handleDelete(selectedNotice._id);
                                        }}
                                    >
                                        <FaTrash /> Delete Notice
                                    </button>
                                    <button
                                        className="noticeadd-btn noticeadd-btn-primary"
                                        onClick={() => {
                                            setShowViewModal(false);
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Confirmation Modal */}
            {showDeleteModal && noticeToDelete && (
                <div className="noticeadd-modal-overlay">
                    <div className="noticeadd-modal noticeadd-delete-modal">
                        <div className="noticeadd-modal-header">
                            <h2 className="noticeadd-modal-title">Confirm Deletion</h2>
                            <button
                                className="noticeadd-modal-close"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="noticeadd-modal-content">
                            <div className="noticeadd-delete-confirmation">
                                <FaExclamationTriangle className="noticeadd-delete-icon" />
                                <h3>Are you sure you want to delete this notice?</h3>
                                <p>Title: <strong>{noticeToDelete.title}</strong></p>
                                <p>This action cannot be undone. The notice will be permanently removed from the system.</p>

                                <div className="noticeadd-form-actions">
                                    <button
                                        type="button"
                                        className="noticeadd-btn noticeadd-btn-secondary"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="noticeadd-btn noticeadd-btn-danger"
                                        onClick={executeDelete}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Deleting...' : 'Yes, Delete Notice'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NoticesAdd;